
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export type ComponentDocumentation = {
  id: string;
  component_id: string;
  title: string;
  content: any;
  version: number;
  created_at: string;
  updated_at: string;
  is_published: boolean;
};

export const fetchDocumentation = async (componentId: string): Promise<ComponentDocumentation | null> => {
  try {
    // Use as any to work around the TypeScript definition limitations
    const { data, error } = await (supabase as any)
      .from("component_documentation")
      .select("*")
      .eq("component_id", componentId)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // Documento não encontrado (código para "não existente" no supabase)
        return null;
      }
      throw error;
    }

    return data as ComponentDocumentation;
  } catch (error) {
    console.error("Error fetching documentation:", error);
    toast({
      title: "Erro",
      description: "Não foi possível carregar a documentação",
      variant: "destructive",
    });
    return null;
  }
};

export const createDocumentation = async (
  componentId: string,
  title: string,
  content: any
): Promise<ComponentDocumentation | null> => {
  try {
    // Use as any to work around the TypeScript definition limitations
    const { data, error } = await (supabase as any)
      .from("component_documentation")
      .insert([
        {
          component_id: componentId,
          title,
          content,
          is_published: false
        }
      ])
      .select()
      .single();

    if (error) throw error;

    toast({
      title: "Documentação criada",
      description: "A documentação foi criada com sucesso",
    });
    
    return data as ComponentDocumentation;
  } catch (error) {
    console.error("Error creating documentation:", error);
    toast({
      title: "Erro",
      description: "Não foi possível criar a documentação",
      variant: "destructive",
    });
    return null;
  }
};

export const updateDocumentation = async (
  documentationId: string,
  title: string,
  content: any,
  isPublished?: boolean
): Promise<ComponentDocumentation | null> => {
  try {
    const updateData: any = { title, content };
    
    if (isPublished !== undefined) {
      updateData.is_published = isPublished;
    }
    
    // Use as any to work around the TypeScript definition limitations
    const { data, error } = await (supabase as any)
      .from("component_documentation")
      .update(updateData)
      .eq("id", documentationId)
      .select()
      .single();

    if (error) throw error;

    toast({
      title: "Documentação atualizada",
      description: "As alterações foram salvas com sucesso",
    });
    
    return data as ComponentDocumentation;
  } catch (error) {
    console.error("Error updating documentation:", error);
    toast({
      title: "Erro",
      description: "Não foi possível atualizar a documentação",
      variant: "destructive",
    });
    return null;
  }
};

export const fetchDocumentationHistory = async (documentationId: string) => {
  try {
    // Use as any to work around the TypeScript definition limitations
    const { data, error } = await (supabase as any)
      .from("component_documentation_history")
      .select("*")
      .eq("documentation_id", documentationId)
      .order("version", { ascending: false });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error("Error fetching documentation history:", error);
    toast({
      title: "Erro",
      description: "Não foi possível carregar o histórico da documentação",
      variant: "destructive",
    });
    return [];
  }
};
